import { useMutation, useQuery, useQueryClient, useInfiniteQuery } from "@tanstack/react-query";
import {
    searchPodcastsService,
    getPodcastByIdService,
    getPodcastEpisodesService,
    getLanguagesService,
    createFilterPresetService,
    getFilterPresetsService,
    getFilterPresetByIdService,
    updateFilterPresetService,
    deleteFilterPresetService,
    searchPodcastsByEpisodeKeywordsService,
    revealPodcastEmailService,
    getEmailRevealUsageService,
    getEmailRevealHistoryService,
    redeemCouponService
} from "../services/podcast-service";
import { PodcastSearchRequest, UpdateFilterPresetRequest } from "../types/podcast";

// Podcasts
export function useSearchPodcasts(request: PodcastSearchRequest, enabled: boolean = false) {
    return useQuery({
        queryKey: ['podcasts-search', request],
        queryFn: () => searchPodcastsService(request),
        enabled: enabled,
        staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    });
}

export function useSearchPodcastsByEpisodeKeywords(keywords: string[], limitRecentEpisodes: number = 10, limit: number = 10, enabled: boolean = false) {
    return useQuery({
        queryKey: ['podcasts-episode-search', keywords, limitRecentEpisodes, limit],
        queryFn: () => searchPodcastsByEpisodeKeywordsService(keywords, limitRecentEpisodes, limit),
        enabled: enabled && keywords.length > 0,
        staleTime: 1000 * 60 * 5,
    });
}

// Mutation version for manual triggers if preferred
export function useSearchPodcastsMutation() {
    return useMutation({
        mutationKey: ['podcasts-search-mutation'],
        mutationFn: searchPodcastsService
    });
}


export function useGetPodcast(id: string) {
    return useQuery({
        queryKey: ['podcast', id],
        queryFn: () => getPodcastByIdService(id),
        enabled: !!id,
    });
}
export function useRevealPodcastEmail() {
    return useMutation({
        mutationFn: (id: string | number) => revealPodcastEmailService(id),
    });
}

export function useGetEmailRevealUsage() {
    return useQuery({
        queryKey: ['email-reveal-usage'],
        queryFn: getEmailRevealUsageService,
    });
}

export function useRedeemCoupon() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (code: string) => redeemCouponService(code),
        onSuccess: () => {
             queryClient.invalidateQueries({ queryKey: ['email-reveal-usage'] });
        }
    });
}

export function useGetEmailRevealHistory(page: number = 0, size: number = 10, startDate?: string, endDate?: string) {
    return useQuery({
        queryKey: ['email-reveal-history', page, size, startDate, endDate],
        queryFn: () => getEmailRevealHistoryService(page, size, startDate, endDate),
        placeholderData: (previousData) => previousData, 
    });
}

export function useGetPodcastEpisodes(id: string, limit: number = 10, enabled: boolean = true) {
    return useQuery({
        queryKey: ['podcast-episodes', id, limit],
        queryFn: () => getPodcastEpisodesService(id, limit),
        enabled: enabled && !!id,
    });
}

export function useGetPodcastEpisodesInfinite(id: string, limit: number = 20) {
    return useInfiniteQuery({
        queryKey: ['podcast-episodes-infinite', id, limit],
        queryFn: ({ pageParam = 0 }) => getPodcastEpisodesService(id, limit, pageParam as number),
        initialPageParam: 0,
        getNextPageParam: (lastPage, allPages) => {
            // lastPage is ApiResponse<Episode[]>
            if (!lastPage.success || !lastPage.data || lastPage.data.length < limit) {
                return undefined;
            }
            return allPages.length;
        },
        enabled: !!id,
    });
}

export function useGetLanguages() {
    return useQuery({
        queryKey: ['podcast-languages'],
        queryFn: getLanguagesService,
    });
}

// Filter Presets
export function useCreateFilterPreset() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: ['create-filter-preset'],
        mutationFn: createFilterPresetService,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['filter-presets'] });
        }
    });
}

export function useGetFilterPresets() {
    return useQuery({
        queryKey: ['filter-presets'],
        queryFn: getFilterPresetsService,
    });
}

export function useGetFilterPreset(id: string) {
    return useQuery({
        queryKey: ['filter-preset', id],
        queryFn: () => getFilterPresetByIdService(id),
        enabled: !!id,
    });
}

export function useUpdateFilterPreset() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: ['update-filter-preset'],
        mutationFn: ({ id, payload }: { id: string, payload: UpdateFilterPresetRequest }) => updateFilterPresetService(id, payload),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['filter-presets'] });
        }
    });
}

export function useDeleteFilterPreset() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: ['delete-filter-preset'],
        mutationFn: deleteFilterPresetService,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['filter-presets'] });
        }
    });
}
